import React from 'react';
import { Tooltip } from '@material-ui/core';
import { Flex, Margin, Padding } from '../base/layout-components';
import DeploymentPipeline from '../DeploymentPipeline';
import BoldedValue from '../BoldedValue';
import Text from '../base/Text';
import { ScaledTooltipWrapper } from './TooltipWrapper';
import { resetTooltipStyle } from './helpers';
import Icon from 'components/base/Icon';
import { METRICS_CALCULATION_STRATEGY_LABEL } from '@linearb/linjs-common';
import { getColor } from 'common/constants/_colors';

const getDescription = ({ isAvg = true, metricsCalculationConfig }) => {
  const { metricsCalculationStrategy = 'avg' } = metricsCalculationConfig;

  if (isAvg) {
    return `${METRICS_CALCULATION_STRATEGY_LABEL[metricsCalculationStrategy]} cycle time`.toUpperCase();
  }
  return null;
};

const CycleTimeTooltip = ({
  title,
  totalTime,
  configurations,
  isAvg,
  scale = 1,
  metricsCalculationConfig = {}
}) => {
  const description = getDescription({ isAvg, metricsCalculationConfig });

  return (
    <ScaledTooltipWrapper scale={scale} title={title} width='40rem'>
      <Padding padding='medium' r='big'>
        <BoldedValue
          value={{ size: 'x-x-large', text: totalTime }}
          description={{ size: 'small', text: description }}
          mb='small'
        />
        <DeploymentPipeline
          configurations={configurations}
          height='6rem'
          enableDescription={false}
        />
      </Padding>
    </ScaledTooltipWrapper>
  );
};

const CycleTimeTooltipContent = ({
  description,
  totalTime,
  configurations,
  isHideNonWorkingDaysEnabled,
  includedStages,
  extraInfo
}) => {
  return (
    <>
      <BoldedValue
        description={{ size: 'small', text: description }}
        value={{ size: 'x-large', text: totalTime }}
        mb='small'
      />
      {configurations.length > 0 && (
        <DeploymentPipeline
          configurations={configurations}
          height='6rem'
          enableDescription={false}
          isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
          includedStages={includedStages}
        />
      )}
      {extraInfo && (
        <Margin t='medium'>
          <Text lineHeight='1.5' size='medium'>
            {extraInfo}
          </Text>
        </Margin>
      )}
    </>
  );
};

const CycleTimeTab = ({ title, isSelected, onClick, dataTestid }) => {
  const borderColor = isSelected ? 'dodgerBlue' : 'silverSand';
  const borderWidth = isSelected ? '2px' : '1px';

  return (
    <Flex
      data-testid={dataTestid}
      cursor='pointer'
      pb='x-x-small'
      flex={1}
      justify='center'
      onClick={!isSelected && onClick}
      borderBottom={`${borderWidth} solid ${getColor(borderColor)}`}
    >
      {title}
    </Flex>
  );
};

const CYCLE_TIME_TAB_VALUE = {
  ACCOUNTED: 'accounted',
  TOTAL: 'total'
};

export const TabbedCycleTimeContent = ({
  description,
  totalTime,
  configurations,
  isHideNonWorkingDaysEnabled,
  includedStages,
  extraInfo,
  accountedCycleTime
}) => {
  const [tabValue, setTabValue] = React.useState(CYCLE_TIME_TAB_VALUE.TOTAL);

  return (
    <Flex direction='column' data-testid='cycle-time-tooltip-tabs-data-testid'>
      <Flex justify='center' direction='row' mb='small'>
        <CycleTimeTab
          title='Total Cycle Time'
          isSelected={tabValue === CYCLE_TIME_TAB_VALUE.TOTAL}
          dataTestid='total-cycle-time-tab-data-testid'
          onClick={() => setTabValue(CYCLE_TIME_TAB_VALUE.TOTAL)}
        />
        <CycleTimeTab
          title='Accounted Cycle Time'
          dataTestid='accounted-cycle-time-tab-data-testid'
          isSelected={tabValue === CYCLE_TIME_TAB_VALUE.ACCOUNTED}
          onClick={() => setTabValue(CYCLE_TIME_TAB_VALUE.ACCOUNTED)}
        />
      </Flex>
      <CycleTimeTooltipContent
        description={description}
        totalTime={
          tabValue === CYCLE_TIME_TAB_VALUE.TOTAL
            ? totalTime
            : accountedCycleTime
        }
        configurations={configurations}
        isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
        includedStages={
          tabValue === CYCLE_TIME_TAB_VALUE.TOTAL ? null : includedStages
        }
        extraInfo={extraInfo}
      />
    </Flex>
  );
};
export const CycleTimePopupTooltip = ({
  title,
  subtitle,
  totalTime,
  configurations = [],
  scale = 1,
  children,
  extraInfo,
  metricsCalculationConfig = {},
  isHideNonWorkingDaysEnabled,
  includedStages = null,
  showAccountedTab,
  accountedCycleTime = null,
  stats
}) => {
  const description = getDescription({ metricsCalculationConfig });
  const CycleTime = (
    <ScaledTooltipWrapper scale={scale} width='46rem'>
      <Padding padding='medium' r='x-big' b='big'>
        <Margin b='medium'>{title}</Margin>
        {subtitle ? <Margin b='medium'>{subtitle}</Margin> : <></>}
        {showAccountedTab ? (
          <TabbedCycleTimeContent
            description={description}
            totalTime={totalTime}
            configurations={configurations}
            isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
            includedStages={includedStages}
            extraInfo={extraInfo}
            accountedCycleTime={accountedCycleTime}
          />
        ) : (
          <CycleTimeTooltipContent
            description={description}
            totalTime={totalTime}
            configurations={configurations}
            isHideNonWorkingDaysEnabled={isHideNonWorkingDaysEnabled}
            includedStages={includedStages}
            extraInfo={extraInfo}
          />
        )}
      </Padding>
    </ScaledTooltipWrapper>
  );

  return (
    <Tooltip classes={resetTooltipStyle()} title={CycleTime} interactive={true}>
      {children}
    </Tooltip>
  );
};

export const PMCodingTimePopupTooltip = ({
  title,
  duration,
  scale = 1,
  codingTimeProviderKey,
  children
}) => {
  const PMCodingTimePopupTooltip = (
    <ScaledTooltipWrapper scale={scale} width='29rem'>
      <Padding padding='medium'>
        <Flex items='center' content='center'>
          <Icon name='CycleTime' size='small' color='shuttleGray' />
          <Text size='medium' pl='0.5rem' weight='500'>
            {title}
          </Text>
        </Flex>
        <Text pt='0.8rem' size='medium' color='shuttleGray' align='left'>
          {duration}
        </Text>
        <Text pt='1rem' size='medium' align='left'>
          Calculated based on {codingTimeProviderKey} time spent “In Progress”
        </Text>
      </Padding>
    </ScaledTooltipWrapper>
  );

  return (
    <Tooltip classes={resetTooltipStyle()} title={PMCodingTimePopupTooltip}>
      {children}
    </Tooltip>
  );
};

export default CycleTimeTooltip;
